import * as React from "react";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import Link from "@mui/material/Link";
import Tooltip from "@mui/material/Tooltip";
import { useQuery } from "react-query";
import {
  JobRequest,
  JobRequestFileCategory,
  JobRequestsService,
  JobRequestStatus,
  JobRequestType,
  MachineTranslationService
} from "gen/clients/llts";
import { LoadingPage } from "components/LoadingPage/LoadingPage";
import { ErrorPage } from "components/ErrorPage/ErrorPage";
import { LabelValue } from "components/LabelValue/LabelValue";
import { formatDate } from "utils/dateUtils";
import Alert from "@mui/material/Alert";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import DownloadIcon from "@mui/icons-material/Download";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import { useTranslation } from "react-i18next";
import { InfoIcon } from "components/InfoIcon/InfoIcon";
import { useDialogState } from "hooks/useDialogState/useDialogState";
import { DownloadTranslatedFilesDialog } from "components/jobRequests/DownloadTranslatedFilesDialog/DownloadTranslatedFilesDialog";
import { RequestHumanTranslationPanel } from "./RequestHumanTranslationPanel/RequestHumanTranslationPanel";
import { JobRequestState } from "components/jobRequests/JobRequestFilter/JobRequestState";
import { JobRequestNameEditable } from "./components/JobRequestNameEditable";
import { JobRequestUtils } from "utils/jobRequestUtils";
import { JobRequestFileList } from "components/jobRequests/JobRequestFileList/JobRequestFileList";
import { LinguisticQualityReview } from "./components/LinguisticQualityReview";
import { SendToAiToolboxDialog } from "./components/SendToAiToolboxDialog";
import { useLltsBranch } from "hooks/useLltsBranch";
import { formatPriceWithCurrency } from "utils/currencyUtils";

/** File extensions supported by AI Toolbox. Can be extended as more formats are supported. */
const AI_TOOLBOX_SUPPORTED_EXTENSIONS = ["docx", "pptx", "pdf", "xlsx"];
/** List of custom field mappings to hide when human escalation is priced. */
const HUMAN_ESCALATION_HIDDEN_CUSTOM_FIELDS = ["Timeline"];

interface Props {
  jobRequestId: string;
  onReturnClick: () => void;
  humanTranslationInfoMessage?: string;
  translatedFilesHeader?: string;
  otherFilesLabel?: string;
  translatedFilesCategoryOverride?: JobRequestFileCategory;
  hideReasonOptions?: boolean;
  hideNotes?: boolean;
  /** Whether to show AI Toolbox features (checkboxes and Send to AI Toolbox button). Defaults to true. */
  showAiToolboxFeatures?: boolean;
  /** Whether to enable file selection. Defaults to false. */
  isFileSelectionEnabled?: boolean;
}

const MtJobRequestDetailsPage: React.FC<Props> = ({
  jobRequestId,
  onReturnClick,
  humanTranslationInfoMessage,
  translatedFilesHeader,
  otherFilesLabel,
  translatedFilesCategoryOverride,
  hideReasonOptions,
  hideNotes,
  showAiToolboxFeatures = true,
  isFileSelectionEnabled
}) => {
  const { t } = useTranslation();
  const branch = useLltsBranch();
  const [selectedTranslatedFileKeys, setSelectedTranslatedFileKeys] = React.useState<string[]>([]);
  const [isRequestHumanTranslationPanelOpen, openRequestHumanTranslationPanel, closeRequestHumanTranslationPanel] =
    useDialogState();
  const [isDownloadTranslatedFilesDialogOpen, openDownloadTranslatedFilesDialog, closeDownloadTranslatedFilesDialog] =
    useDialogState();
  const [isEnhanceTranslationDialogOpen, openEnhanceTranslationDialog, closeEnhanceTranslationDialog] =
    useDialogState();

  const {
    data: jobRequest,
    isLoading,
    error,
    refetch
  } = useQuery(["getJobRequest", jobRequestId], {
    queryFn: () => JobRequestsService.getJobRequest({ jobRequestId }),
    refetchInterval: (jr: JobRequest | undefined) => (JobRequestUtils.isInProgress(jr?.status) ? 10000 : false),
    cacheTime: 0
  });

  // Escalation to human is enabled only if file selection is enabled and the job request is completed or failed
  const isEscalationToHumanEnabled =
    isFileSelectionEnabled &&
    (jobRequest?.status === JobRequestStatus.COMPLETED || jobRequest?.status === JobRequestStatus.FAILED);

  const { data: mtSettings } = useQuery(["getMtSettings"], {
    queryFn: MachineTranslationService.getMachineTranslationSettings
  });

  const getLanguageName = React.useCallback(
    (languageId: string | number | undefined) => {
      if (!languageId) {
        return null;
      }
      const languages = [...(jobRequest?.targetLanguages || [])];
      if (jobRequest?.sourceLanguage?.id === languageId) {
        languages.push(jobRequest.sourceLanguage);
      }
      const language = languages.find(l => l.id === languageId);
      return language?.name || `${languageId}`;
    },
    [jobRequest]
  );

  const sourceFiles = React.useMemo(
    () =>
      jobRequest?.files
        ?.filter(f => f.category === JobRequestFileCategory.SOURCE_DOCUMENT)
        ?.sort((f1, f2) =>
          f1.fileName
            .substring(0, f1.fileName.lastIndexOf("."))
            .localeCompare(f2.fileName.substring(0, f2.fileName.lastIndexOf(".")))
        ),
    [jobRequest]
  );

  const otherFiles = React.useMemo(
    () =>
      jobRequest?.files
        ?.filter(f => f.category === JobRequestFileCategory.OTHER)
        ?.sort((f1, f2) =>
          f1.fileName
            .substring(0, f1.fileName.lastIndexOf("."))
            .localeCompare(f2.fileName.substring(0, f2.fileName.lastIndexOf(".")))
        ),
    [jobRequest]
  );

  const translatedFiles = React.useMemo(
    () =>
      jobRequest?.files
        ?.filter(f => f.category === JobRequestFileCategory.TRANSLATED_DOCUMENT)
        ?.sort((f1, f2) =>
          f1.fileName
            .substring(0, f1.fileName.lastIndexOf("_"))
            .localeCompare(f2.fileName.substring(0, f2.fileName.lastIndexOf("_")))
        ),
    [jobRequest]
  );

  const filesForHumanTranslation = React.useMemo(() => {
    if (!isEscalationToHumanEnabled) {
      return jobRequest?.files || [];
    }
    const selectedTranslatedFiles = translatedFiles?.filter(f => selectedTranslatedFileKeys.includes(f.fileKey)) || [];
    // Find source files that match the selected translated files by name up to the last underscore (_)
    const sourceFilesToInclude =
      sourceFiles?.filter(sf => {
        const translatedFile = selectedTranslatedFiles.find(tf => {
          const tfBaseName = tf.fileName.substring(0, tf.fileName.lastIndexOf("_"));
          const sfBaseName = sf.fileName.substring(0, sf.fileName.lastIndexOf("."));
          return tfBaseName === sfBaseName;
        });
        return !!translatedFile;
      }) || [];
    return [...sourceFilesToInclude, ...selectedTranslatedFiles, ...(otherFiles || [])];
  }, [
    isEscalationToHumanEnabled,
    translatedFiles,
    sourceFiles,
    otherFiles,
    jobRequest?.files,
    selectedTranslatedFileKeys
  ]);

  const targetLanguageIdsForHumanTranslation = React.useMemo(() => {
    if (!isEscalationToHumanEnabled) {
      return jobRequest?.targetLanguages?.map(l => l.id as string) || [];
    }
    const selectedTranslatedFiles = translatedFiles?.filter(f => selectedTranslatedFileKeys.includes(f.fileKey)) || [];
    const uniqueLanguageIds = new Set(selectedTranslatedFiles.map(f => f.languageId).filter(Boolean));
    return Array.from(uniqueLanguageIds) as string[];
  }, [isEscalationToHumanEnabled, translatedFiles, selectedTranslatedFileKeys, jobRequest?.targetLanguages]);

  const humanEscalationPricingEnabled = false; // TODO: temporarily disabled. !!mtSettings?.enableHumanEscalationPricing;

  /**
   * Calculates the estimated price for human escalation based on the linguistic quality score
   * and word count of selected translated files. This memoized value updates whenever
   * the machine translation settings, selected file keys, or the list of translated files change.
   *
   * The calculation considers:
   * - The linguistic quality score of each file, rounded to the nearest integer and clamped between 1 and 5.
   * - Human escalation price defined for each quality rating (1-5).
   * - The total word count, estimated by dividing the character count by 5.
   *
   * Returns `undefined` if any of the following conditions are met:
   * - No file has a linguistic quality score.
   * - Human escalation price mapping is unavailable in machine translation settings.
   * - No translated files have been selected.
   *
   * @constant {number | undefined} estimatedHumanEscalationPrice
   */
  const estimatedHumanEscalationPrice = React.useMemo(() => {
    if (!humanEscalationPricingEnabled || !isEscalationToHumanEnabled) {
      return undefined;
    }
    const anyFileHasLinguisticQualityScore = translatedFiles?.some(
      f => f.linguisticQualityScore !== undefined && f.linguisticQualityScore !== null
    );
    if (!anyFileHasLinguisticQualityScore) {
      return undefined;
    }
    const priceByRating = mtSettings?.humanEscalationPriceByRating;
    const selectedFiles = translatedFiles?.filter(f => selectedTranslatedFileKeys.includes(f.fileKey)) || [];
    if (!priceByRating || selectedFiles.length === 0) {
      return undefined;
    }

    // Calculate total price based on each file's rating and characters
    let totalPrice = 0;
    for (const file of selectedFiles) {
      const rating = Math.round(file.linguisticQualityScore || 1);
      const ratingKey = String(Math.max(1, Math.min(5, rating))) as "1" | "2" | "3" | "4" | "5";
      const pricePerWord = priceByRating[ratingKey];
      // Convert characters to words (approximately 5 characters per word)
      const wordCount = Math.ceil((file.charactersCharged || 0) / 5);
      totalPrice += wordCount * pricePerWord;
    }

    return totalPrice;
  }, [
    humanEscalationPricingEnabled,
    isEscalationToHumanEnabled,
    mtSettings?.humanEscalationPriceByRating,
    selectedTranslatedFileKeys,
    translatedFiles
  ]);

  const onDownloadAllClick = React.useCallback(async () => {
    openDownloadTranslatedFilesDialog();
  }, [openDownloadTranslatedFilesDialog]);

  const onEnhanceTranslationClick = React.useCallback(() => {
    openEnhanceTranslationDialog();
  }, [openEnhanceTranslationDialog]);

  const onRequestHumanTranslationSuccess = React.useCallback(() => {
    refetch();
    closeRequestHumanTranslationPanel();
  }, [closeRequestHumanTranslationPanel, refetch]);

  const areSelectedFilesValidForAiToolbox = React.useCallback(() => {
    if (selectedTranslatedFileKeys.length === 0) {
      return false;
    }
    // Check if all selected files have supported extensions
    const selectedFiles = translatedFiles?.filter(f => selectedTranslatedFileKeys.includes(f.fileKey)) || [];
    return selectedFiles.every(file => {
      const extension = file.fileName.split(".").pop()?.toLowerCase();
      return extension && AI_TOOLBOX_SUPPORTED_EXTENSIONS.map(ext => ext.toLowerCase()).includes(extension);
    });
  }, [selectedTranslatedFileKeys, translatedFiles]);

  if (error) {
    return <ErrorPage apiError={error} />;
  }
  if (isLoading || !jobRequest) {
    return <LoadingPage />;
  }
  return (
    <Stack>
      <Stack direction="row" justifyItems="center" alignItems="center" spacing={1}>
        <Button
          startIcon={<ArrowBackIosIcon />}
          onClick={onReturnClick}
          variant="text"
          sx={{ whiteSpace: "nowrap", textTransform: "none" }}
        >
          {t("machineTranslation.fileTranslation.actions.backToRequests")}
        </Button>
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          |
        </Typography>
        <Box flexGrow={1}>
          <JobRequestNameEditable jobRequest={jobRequest} onChange={refetch} />
        </Box>
      </Stack>
      <Card sx={{ p: 2, mt: 1 }}>
        <Grid container={true} spacing={1}>
          <Grid item={true} xs={12} sm={6}>
            <Stack spacing={1}>
              <LabelValue
                label="Status"
                value={
                  <Stack direction="row" spacing={1}>
                    <JobRequestState status={jobRequest.status} />
                    {jobRequest.status === JobRequestStatus.SUBMITTED_TO_XTRF && (
                      <InfoIcon tooltip={t("machineTranslation.fileTranslation.requests.submittedToXtrfStatusInfo")} />
                    )}
                    {jobRequest.status === JobRequestStatus.CREATED && (
                      <InfoIcon tooltip={t("machineTranslation.fileTranslation.requests.createdStatusInfo")} />
                    )}
                    {jobRequest.status === JobRequestStatus.FAILED && (
                      <InfoIcon tooltip={t("machineTranslation.fileTranslation.requests.failedStatusInfo")} />
                    )}
                  </Stack>
                }
              />
              {jobRequest.xtrfInternalId && (
                <LabelValue
                  label={t("machineTranslation.fileTranslation.requests.humanTranslationProject")}
                  value={
                    <Link
                      href={`${import.meta.env.VITE_XTRF_HOME}/customers/main.html#!${jobRequest.requestType === JobRequestType.QUOTE_REQUEST ? "quote" : "project"}/${jobRequest.xtrfInternalId}`}
                      target="_blank"
                    >
                      <Typography>{jobRequest.xtrfName}</Typography>
                    </Link>
                  }
                />
              )}
              <LabelValue
                label={t("machineTranslation.fileTranslation.requests.submittedOn")}
                value={<Typography>{formatDate(jobRequest.createTime)}</Typography>}
              />
            </Stack>
          </Grid>
          <Grid item={true} xs={12} sm={6}>
            <Stack spacing={1}>
              <LabelValue
                label={t("machineTranslation.fileTranslation.requests.sourceLanguage")}
                value={<Typography>{jobRequest.sourceLanguage?.name}</Typography>}
              />
              <LabelValue
                label={t("machineTranslation.fileTranslation.requests.targetLanguages")}
                value={
                  <Typography>
                    {jobRequest.targetLanguages
                      ?.map(l => l.name)
                      .sort((l1, l2) => l1.localeCompare(l2))
                      .join(", ")}
                  </Typography>
                }
              />
            </Stack>
          </Grid>
        </Grid>
      </Card>

      <Grid container={true} spacing={2}>
        <Grid item={true} xs={12} sm={6}>
          <Card sx={{ mt: 2, height: 400 }}>
            <CardHeader
              title={
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="h6">{t("machineTranslation.fileTranslation.requests.sourceFiles")}</Typography>
                  <Box sx={{ height: 40, paddingTop: "5px" }}>
                    <Typography variant="body1">{jobRequest.sourceLanguage?.name}</Typography>
                  </Box>
                </Stack>
              }
            />
            <CardContent sx={{ overflowY: "auto", pt: 0 }}>
              <JobRequestFileList jobRequestId={jobRequestId} files={sourceFiles} />
              {(otherFiles?.length || 0) > 0 && (
                <Stack mt={3}>
                  <Typography sx={theme => ({ color: theme.palette.grey["700"], fontWeight: 600 })}>
                    {otherFilesLabel || t("machineTranslation.fileTranslation.requests.otherFiles")}
                  </Typography>
                  <JobRequestFileList jobRequestId={jobRequestId} files={otherFiles} />
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item={true} xs={12} sm={6}>
          <Card sx={{ mt: 2, height: 400, display: "flex", flexDirection: "column" }}>
            <CardHeader
              title={
                <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
                  <Typography variant="h6">
                    {translatedFilesHeader || t("machineTranslation.fileTranslation.requests.mtTranslatedFiles")}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {showAiToolboxFeatures &&
                      mtSettings?.enableAiToolbox &&
                      translatedFiles &&
                      translatedFiles.length > 0 &&
                      !JobRequestUtils.isInProgress(jobRequest.status) && (
                        <Tooltip
                          title={t("machineTranslation.fileTranslation.requests.selectFilesTooltip") || ""}
                          disableHoverListener={areSelectedFilesValidForAiToolbox()}
                        >
                          <span>
                            <Button
                              startIcon={<AutoFixHighIcon />}
                              variant="contained"
                              onClick={onEnhanceTranslationClick}
                              size="small"
                              disabled={!areSelectedFilesValidForAiToolbox()}
                            >
                              {t("machineTranslation.fileTranslation.requests.sendToAiToolboxBtn")}
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                  </Stack>
                </Stack>
              }
            />
            <CardContent sx={{ overflowY: "auto", pt: 0, flex: 1, display: "flex", flexDirection: "column" }}>
              <Box sx={{ flex: 1, overflowY: "auto" }}>
                {!translatedFiles || translatedFiles.length === 0 ? (
                  <Typography variant="body2">{t("machineTranslation.fileTranslation.requests.noFiles")}</Typography>
                ) : (
                  <JobRequestFileList
                    jobRequestId={jobRequestId}
                    files={translatedFiles}
                    showAiLinguisticQualityScorePerFile={
                      mtSettings?.enableAiLinguisticQualityReview && mtSettings?.showAiLinguisticQualityScorePerFile
                    }
                    selectable={isFileSelectionEnabled}
                    selectedFileKeys={selectedTranslatedFileKeys}
                    onSelectionChange={setSelectedTranslatedFileKeys}
                    groupByLanguage={true}
                    getLanguageName={getLanguageName}
                    linguisticQualityReviewResults={
                      mtSettings?.enableAiLinguisticQualityReview
                        ? jobRequest.linguisticQualityReviewResults
                        : undefined
                    }
                  />
                )}
              </Box>

              {/* AI Linguistic Review Section - positioned at bottom */}
              {(() => {
                // Calculate average score across all rated files
                const ratedFiles = translatedFiles?.filter(
                  f => f.linguisticQualityScore !== undefined && f.linguisticQualityScore !== null
                );
                const averageScore =
                  ratedFiles && ratedFiles.length > 0
                    ? ratedFiles.reduce((sum, f) => sum + f.linguisticQualityScore!, 0) / ratedFiles.length
                    : undefined;

                return mtSettings?.enableAiLinguisticQualityReview && averageScore !== undefined ? (
                  <Box sx={{ mt: 2, alignSelf: "flex-end", width: "100%" }}>
                    <LinguisticQualityReview averageScore={averageScore} />
                    {isEscalationToHumanEnabled &&
                      estimatedHumanEscalationPrice !== undefined &&
                      estimatedHumanEscalationPrice > 0 && (
                        <Typography
                          variant="body2"
                          sx={{ mt: 0.5 }}
                          dangerouslySetInnerHTML={{
                            __html: t("machineTranslation.fileTranslation.requests.estimatedHumanExpertServicesCost", {
                              price: formatPriceWithCurrency(estimatedHumanEscalationPrice, branch?.currency || "USD")
                            })
                          }}
                        />
                      )}
                  </Box>
                ) : null;
              })()}
            </CardContent>
          </Card>
          {translatedFiles && translatedFiles.length > 0 && !JobRequestUtils.isInProgress(jobRequest.status) && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
              <Button startIcon={<DownloadIcon />} variant="outlined" onClick={onDownloadAllClick} size="small">
                {t("machineTranslation.fileTranslation.requests.downloadAllBtn")}
              </Button>
            </Box>
          )}
        </Grid>

        <Grid item={true} xs={12}>
          {!(jobRequest.status === JobRequestStatus.COMPLETED || jobRequest.status === JobRequestStatus.FAILED) && (
            <Box textAlign="center">
              <Button onClick={onReturnClick} variant="outlined" sx={{ whiteSpace: "nowrap", textTransform: "none" }}>
                {t("machineTranslation.fileTranslation.actions.return")}
              </Button>
            </Box>
          )}
          {(jobRequest.status === JobRequestStatus.COMPLETED || jobRequest.status === JobRequestStatus.FAILED) &&
            !mtSettings?.disableHumanEscalation && (
              <Stack spacing={2}>
                {!isEscalationToHumanEnabled && (
                  <Stack spacing={2}>
                    <Alert severity="info" variant="filled">
                      <div
                        dangerouslySetInnerHTML={{
                          __html:
                            humanTranslationInfoMessage ||
                            t("machineTranslation.fileTranslation.requests.humanTranslationInfo")
                        }}
                      />
                    </Alert>
                    <Box textAlign="center">
                      <Button onClick={onReturnClick} sx={{ mr: 1, whiteSpace: "nowrap", textTransform: "none" }}>
                        {t("machineTranslation.fileTranslation.actions.return")}
                      </Button>
                      <Button variant="contained" size="large" onClick={openRequestHumanTranslationPanel}>
                        {t("machineTranslation.fileTranslation.actions.requestHumanTranslation")}
                      </Button>
                    </Box>
                  </Stack>
                )}
                {isEscalationToHumanEnabled && (
                  <Stack spacing={2}>
                    <Alert severity="info" variant="filled">
                      {selectedTranslatedFileKeys.length > 0 ? (
                        estimatedHumanEscalationPrice ? (
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            <div
                              dangerouslySetInnerHTML={{
                                __html:
                                  humanTranslationInfoMessage ||
                                  t("machineTranslation.fileTranslation.requests.estimatedHumanEscalationPrice", {
                                    price: formatPriceWithCurrency(
                                      estimatedHumanEscalationPrice,
                                      branch?.currency || "USD"
                                    )
                                  })
                              }}
                            />
                          </Typography>
                        ) : (
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            <div
                              dangerouslySetInnerHTML={{
                                __html:
                                  humanTranslationInfoMessage ||
                                  t("machineTranslation.fileTranslation.requests.humanTranslationInfo")
                              }}
                            />
                          </Typography>
                        )
                      ) : (
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {t("machineTranslation.fileTranslation.requests.selectFilesForHumanTranslation")}
                        </Typography>
                      )}
                    </Alert>
                    <Box textAlign="center">
                      <Button onClick={onReturnClick} sx={{ mr: 1, whiteSpace: "nowrap", textTransform: "none" }}>
                        {t("machineTranslation.fileTranslation.actions.return")}
                      </Button>
                      <Tooltip
                        title={
                          isEscalationToHumanEnabled && selectedTranslatedFileKeys.length === 0
                            ? t("machineTranslation.fileTranslation.requests.selectFilesTooltip") || ""
                            : ""
                        }
                        disableHoverListener={selectedTranslatedFileKeys.length > 0}
                      >
                        <span>
                          <Button
                            variant="contained"
                            size="large"
                            onClick={openRequestHumanTranslationPanel}
                            disabled={isEscalationToHumanEnabled && selectedTranslatedFileKeys.length === 0}
                          >
                            {t("machineTranslation.fileTranslation.actions.requestHumanTranslation")}
                            {estimatedHumanEscalationPrice && (
                              <Typography variant="body2" sx={{ fontWeight: 500, ml: 0.5 }}>
                                - {formatPriceWithCurrency(estimatedHumanEscalationPrice, branch?.currency || "USD")}
                              </Typography>
                            )}
                          </Button>
                        </span>
                      </Tooltip>
                    </Box>
                  </Stack>
                )}
              </Stack>
            )}
        </Grid>
      </Grid>

      {isRequestHumanTranslationPanelOpen && jobRequest.sourceLanguage?.id && jobRequest.targetLanguages && (
        <RequestHumanTranslationPanel
          jobRequestId={jobRequestId}
          requestType={
            estimatedHumanEscalationPrice ? JobRequestType.TRANSLATION_REQUEST : JobRequestType.QUOTE_REQUEST
          }
          requestName={jobRequest.name}
          sourceLanguageId={jobRequest.sourceLanguage.id as string}
          targetLanguageIds={targetLanguageIdsForHumanTranslation}
          disableTargetLanguages={!!estimatedHumanEscalationPrice}
          files={filesForHumanTranslation}
          translatedFilesCategoryOverride={translatedFilesCategoryOverride}
          hideReasonOptions={hideReasonOptions}
          createReceivablesBasedOnAiLqr={!!estimatedHumanEscalationPrice}
          estimatedHumanEscalationPrice={estimatedHumanEscalationPrice}
          hideNotes={hideNotes}
          onClose={closeRequestHumanTranslationPanel}
          onSuccess={onRequestHumanTranslationSuccess}
          hiddenCustomFields={estimatedHumanEscalationPrice ? HUMAN_ESCALATION_HIDDEN_CUSTOM_FIELDS : undefined}
        />
      )}
      {isDownloadTranslatedFilesDialogOpen && (
        <DownloadTranslatedFilesDialog
          jobRequestId={jobRequestId}
          zipFileName={`mt-translation-${jobRequestId}.zip`}
          onClose={closeDownloadTranslatedFilesDialog}
          onSuccess={closeDownloadTranslatedFilesDialog}
        />
      )}
      {isEnhanceTranslationDialogOpen && mtSettings && translatedFiles && translatedFiles.length > 0 && (
        <SendToAiToolboxDialog
          open={isEnhanceTranslationDialogOpen}
          onClose={closeEnhanceTranslationDialog}
          jobRequest={jobRequest}
          selectedFiles={translatedFiles.filter(f => selectedTranslatedFileKeys.includes(f.fileKey))}
          mtSettings={mtSettings}
        />
      )}
    </Stack>
  );
};

export { MtJobRequestDetailsPage };
